/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import {
  getBraindatePath,
  getBraindateConversation,
  getBraindateLocation,
} from '@braindate/domain/lib/braindate/util';
import { isVirtualLocation } from '@braindate/domain/lib/location/util';
import { getConversationParticipants } from '@braindate/domain/lib/conversation/util';
import { getUserId, getUserFirstName } from '@braindate/domain/lib/user/util';

import Notification from 'src/shared/app/notification/component/Notification';
import virtualMessages from 'plugin/virtual-braindate/l10n/virtualBraindateL10n';
import messages from 'src/shared/app/notification/l10n/notificationL10n';
import { getNotificationData } from 'src/shared/app/notification/util/notificationUtil';

import {
  isConversationAboutFishbowl,
  isConversationAboutGroupBraindate,
} from 'src/shared/domain/conversation/util/conversationUtil';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';

import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { User } from '@braindate/domain/lib/user/type';
import type { Notification as NotificationType } from 'src/shared/app/notification/type/notificationTypes';

type Props = {
  notification: NotificationType<>,
  user: User,
  handleDismiss: () => void,
  // Self passed
  intl: intlShape,
};

const CheckInNotificationDumb = ({
  notification,
  user,
  handleDismiss,
  intl,
}: Props): React.Node => {
  // $FlowIssue
  const braindate: Braindate = getNotificationData(notification);
  const href = getBraindatePath(braindate);
  const conversation = getBraindateConversation(braindate);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);
  const participants = getConversationParticipants(conversation);
  const otherParticipants = participants.filter(
    (p) => getUserId(p) !== getUserId(user),
  );
  const location = getBraindateLocation(braindate);
  const isVirtual = location && isVirtualLocation(location);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const { braindateSingular } = useEventBraindateName();

  const messageTxt = () => {
    if (isVirtual && isConversationAboutFishbowl(conversation)) {
      return intl.formatMessage(virtualMessages.reminderFishbowl, {
        braindateSingular,
      });
    }
    return intl.formatMessage(
      isVirtual ? virtualMessages.reminder : messages.checkIn,
      {
        isGroupBraindate,
        firstName: otherParticipants.map(getUserFirstName).join(', '),
        braindateSingular,
      },
    );
  };

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <Notification href={href} handleDismiss={handleDismiss}>
      {messageTxt()}
    </Notification>
  );
};

export default compose(React.memo, injectIntl)(CheckInNotificationDumb);
